import { useState, useEffect, useCallback, useRef } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { SearchResults } from '@/components/search/SearchResults';
import { FileUpload } from '@/components/search/FileUpload';
import { RecentSearches } from '@/components/search/RecentSearches';
import { useSearch } from '@/hooks/useSearch';
import { useSearchHistory } from '@/lib/store/searchHistory';

interface SearchBarProps {
  onSearch: (query: string) => void;
  initialQuery?: string;
  placeholder?: string;
}

export function SearchBar({ onSearch, initialQuery = '', placeholder }: SearchBarProps) {
  const [query, setQuery] = useState(initialQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const { search, results, sandboxResults, malwareResults, isLoading, error } = useSearch();
  const [hasSearched, setHasSearched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const addSearch = useSearchHistory(state => state.addSearch);
  const { searches } = useSearchHistory(state => ({ 
    searches: state.searches.filter(s => s.type !== 'vulnerability') 
  }));

  useEffect(() => {
    setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.length >= 2) {
        setDebouncedQuery(query);
        onSearch(query);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (debouncedQuery.length >= 2) {
      search(debouncedQuery);
      setHasSearched(true);
      
      // Detect search type based on query pattern
      const type = /^[a-fA-F0-9]{32,64}$/.test(debouncedQuery) 
        ? 'hash' 
        : /^G\d{4}$/i.test(debouncedQuery)
          ? 'group'
          : 'ioc';
      
      addSearch(debouncedQuery, type);
    }
  }, [debouncedQuery, search]);

  const handleSearch = useCallback((searchQuery: string) => {
    if (searchQuery.length >= 2) {
      setQuery(searchQuery);
      setDebouncedQuery(searchQuery);
      setHasSearched(true);
      onSearch(searchQuery);
    }
  }, [search, onSearch]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(query);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    if (isMobile) {
      handleSearch(e.target.value);
    }
  };

  const handleHash = (hash: string) => {
    setQuery(hash);
    setDebouncedQuery(hash);
  };

  useEffect(() => {
    if (initialQuery && initialQuery.length >= 2) {
      setQuery(initialQuery);
      setDebouncedQuery(initialQuery);
      setHasSearched(true);
    }
  }, [initialQuery]);

  return (
    <div className="p-4 space-y-6 w-full sm:p-6">
      <h2 className="mb-4 font-ibm-condensed text-2xl sm:text-3xl text-foreground">
        Threat Search
      </h2>
      <form onSubmit={handleSubmit} className="w-full" role="search">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            ref={inputRef}
            type="search"
            role="searchbox"
            aria-label="Search threat actors"
            enterKeyHint="search"
            placeholder={placeholder || "Search by any indicator of compromise, TTP, or group name"}
            className="h-12 pl-12 text-base bg-background placeholder:text-muted-foreground w-full"
            value={query}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch(query);
              }
            }}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          />
        </div>
      </form>

      {!query && <RecentSearches onSelect={handleSearch} searches={searches} />}

      <FileUpload onHash={handleHash} />

      {error && (
        <div className="text-sm text-destructive" role="alert">
          {error}
        </div>
      )}

      <SearchResults 
        groups={results}
        sandboxResults={sandboxResults}
        malwareResults={malwareResults}
        isLoading={isLoading}
        hasSearched={hasSearched}
      />
    </div>
  );
}